import { useMaybeStudentDomainsRegexVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useMaybeStudentDomainsRegexVariation.js';
import { useCheckEduInstitution } from '#pplx/frontend/apps/ask/queries/academic-queries/useCheckEduInstitution.js';
import { useUserInfo } from '#pplx/frontend/user-info/useUserInfo.js';
import { useMemo } from 'react';
/**
 * This hook checks if a user/visitor is a student or is likely a student but not 100% certain.
 * It does this by checking:
 * - If the user has a verified student status (always a student)
 * - If the user has an educational email (not always a student)
 * - If the user/visitor is at an educational institution by IP (not always a student)
 *
 */
export const useIsLikelyStudent = ({ reason, }) => {
    // Check if a user verified student status (always a student)
    const { isStudent: isVerifiedStudent, query: userInfoQuery } = useUserInfo({ reason });
    // Check if user has an educational email (not always a student)
    const { variation: isMaybeStudentByEmail, loading: isMaybeStudentByEmailLoading } = useMaybeStudentDomainsRegexVariation({ defaultValue: false });
    // Check institution by IP (not always a student)
    const { data: institutionDataByIp, isLoading: isInstitutionByIpLoading } = useCheckEduInstitution({ reason });
    // useUserInfo uses placeholderData so isLoading is false even while fetching.
    // isPlaceholderData is true until the real API response arrives, so we must
    // treat it as still loading to avoid acting on the default isStudent=false.
    const isUserInfoLoading = userInfoQuery.isPlaceholderData;
    return useMemo(() => {
        const isLoading = isInstitutionByIpLoading || isMaybeStudentByEmailLoading || isUserInfoLoading;
        if (isLoading) {
            return {
                isLoading: true,
                signals: {
                    isVerifiedStudent: null,
                    isPossibleStudentByEmail: null,
                    isPossibleStudentByIP: null,
                },
            };
        }
        return {
            isLoading: false,
            signals: {
                isVerifiedStudent: !!isVerifiedStudent,
                isPossibleStudentByEmail: !!isMaybeStudentByEmail,
                isPossibleStudentByIP: !!institutionDataByIp?.institution,
            },
        };
    }, [
        isInstitutionByIpLoading,
        isMaybeStudentByEmailLoading,
        isUserInfoLoading,
        isMaybeStudentByEmail,
        isVerifiedStudent,
        institutionDataByIp?.institution,
    ]);
};
//# sourceMappingURL=useIsLikelyStudent.js.map